import { FC } from "react";
import TranscodePresetFormProps from "./TranscodePresetForm.interface";
import { InputField } from "../../InputField";
import InputLegend from "../../InputLegend";
import { TranscodePreset, useGetUserQuery } from "../../../features/api/apiSlice";
import { SubmitHandler, useForm } from "react-hook-form";

import styles from "./TranscodePresetForm.module.css";

const TranscodePresetForm: FC<TranscodePresetFormProps> = ({ transcodePreset, formId, onSubmit }) => {
  const { data: user } = useGetUserQuery();
  const maxBitarte = user?.tariff.max_input_bandwidth;
  const { name, fps, width, height, video_bitrate, audio_bitrate, gop } = transcodePreset ?? {};
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError
  } = useForm<Partial<TranscodePreset>>({
    mode: "all"
  });

  const actionPreset: SubmitHandler<Partial<TranscodePreset>> = ({name, ...transcodePresetSettings}) => {
    if (!name?.trim()) {
      setError("name", {type: "custom", message: "Заполните, пожалуйста, это поле."});
      return;
    }

    onSubmit({name, ...transcodePresetSettings});
  };

  return (
    <form id={formId} onSubmit={handleSubmit(actionPreset)} className={styles.form}>
      <InputLegend legend="Название">
        <InputField
          placeholder="Введите название"
          isError={Boolean(errors.name)}
          error={errors.name?.message}
          defaultValue={name}
          {...register("name", {
            required: { value: true, message: "Заполните, пожалуйста, это поле." },
          })}
        />
      </InputLegend>

      <InputLegend legend="FPS">
        <InputField
          placeholder="Значение FPS"
          isError={Boolean(errors.fps)}
          error={errors.fps?.message}
          type="number"
          defaultValue={fps}
          {...register("fps", {
            min: { value: 10, message: "Значение должно быть не меньше 10." },
            max: { value: 60, message: "Значение должно быть не больше 60." },
            required: { value: true, message: "Заполните, пожалуйста, это поле." },
          })}
        />
      </InputLegend>

      <div className={styles["form__double-field"]}>
        <InputLegend legend="Ширина">
          <InputField
            placeholder="Ширина"
            isError={Boolean(errors.width)}
            error={errors.width?.message}
            type="number"
            defaultValue={width}
            {...register("width", {
              min: { value: 640, message: "Значение должно быть не меньше 640." },
              max: { value: 1920, message: "Значение должно быть не больше 1920." },
              required: { value: true, message: "Заполните, пожалуйста, это поле." }
            })}
          />
        </InputLegend>

        <InputLegend legend="Высота">
          <InputField
            placeholder="Высота"
            isError={Boolean(errors.height)}
            error={errors.height?.message}
            type="number"
            defaultValue={height}
            {...register("height", {
              min: { value: 360, message: "Значение должно быть не меньше 360." },
              max: { value: 1080, message: "Значение должно быть не больше 1080." },
              required: { value: true, message: "Заполните, пожалуйста, это поле." }
            })}
          />
        </InputLegend>
      </div>

      <InputLegend legend="Битрейт видео">
        <InputField
          placeholder="Значение битрейта"
          isError={Boolean(errors.video_bitrate)}
          error={errors.video_bitrate?.message}
          type="number"
          defaultValue={video_bitrate}
          {...register("video_bitrate", {
            min: { value: 2500, message: "Значение должно быть не меньше 2500." },
            ...(maxBitarte && { max: { value: maxBitarte * 1000, message: `Значение должно быть не больше ${maxBitarte * 1000}.` } }),
            required: { value: true, message: "Заполните, пожалуйста, это поле." }
          })}
        />
      </InputLegend>

      <InputLegend legend="Битрейт аудио">
        <InputField
          placeholder="Значение битрейта"
          type="number"
          isError={Boolean(errors.audio_bitrate)}
          error={errors.audio_bitrate?.message}
          defaultValue={audio_bitrate}
          {...register("audio_bitrate", {
            min: { value: 32, message: "Значение должно быть не меньше 32." },
            max: { value: 320, message: "Значение должно быть не больше 320." },
            required: { value: true, message: "Заполните, пожалуйста, это поле." }
          })}
        />
      </InputLegend>

      <InputLegend legend="Ключевой кадр">
        <InputField
          placeholder="Введите ключевой кадр"
          isError={Boolean(errors.gop)}
          error={errors.gop?.message}
          type="number"
          defaultValue={gop}
          {...register("gop", {
            min: { value: 1, message: "Значение должно быть не меньше 1." },
            max: { value: 5, message: "Значение должно быть не больше 5." },
            required: { value: true, message: "Заполните, пожалуйста, это поле." }
          })}
        />
      </InputLegend>
    </form>
  );
};

export default TranscodePresetForm;